import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, ElementRef, HostBinding, HostListener, inject, OnDestroy, OnInit, signal, untracked } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { IsActiveMatchOptions, NavigationEnd, Params, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { filter, Subscription } from 'rxjs';
import Keycloak from 'keycloak-js';
import { HxAuthService, PermissionKey } from 'hx-services';
import { environment } from '@callcenter-env/environment';
import { buildMainMenu, MenuItem } from '@callcenter-app/components/sidebar/main-menu';
import { HeaderService } from '@callcenter-app/components/header/header.service';
import { CartTickerService } from '@callcenter-app/components/order-live/cart-ticker.service';
import { CallShellState } from '@callcenter-app/service/call-state.machine';
import { CallStateService } from '@callcenter-app/service/call-state.service';
import { CallLinkService } from '@callcenter-app/service/call-link.service';
import { WORKPLACE_PATH, WORKPLACE_READY, WORKPLACE_SHELL_ENABLED } from '@callcenter-app/service/workplace.config';
import { OrderDraftStore } from '@callcenter-app/pages/order-editor/state/order-draft.store';
import { CALL_WORD, currencySymbol, ORDER_WORD, pluralParams } from '@callcenter-app/pages/orders-archive/orders-archive.format';
import { buildShiftCardView, ShiftCardView } from '../shift-card.view';
import { iconOfLink, SHELL_ICON } from '../shell-icons';
import { MyTodayModel, ShellPollsService } from '../shell-polls.service';
import { ShellUiService } from '../shell-ui.service';
import { ThemeService } from '../theme.service';

export type MenuTone = 'work' | 'ctrl' | 'rep' | 'old';

export interface ShellNavRow {
  kind: 'label' | 'item' | 'oldToggle';
  title: string;
  link?: string;
  queryParams?: Params;
  icon: string;
  tone: MenuTone;
  /** The clock of the call at «Рабочее место». */
  live?: string;
  /** The late orders at «Заказы». */
  badge?: number;
  /** The folded rail: a thin line before the first page of a section. */
  sep?: boolean;
}

/** Below this width the menu is a drawer over the page (as the old menu on a tablet) and never folded. */
export const NARROW_QUERY = '(max-width: 1023px)';

export interface TodayFigures {
  calls: string;
  callsWord: string;
  orders: string;
  ordersWord: string;
  sales: string;
  /** «₸ продажи» — the symbol of the currency the server summed in (the operator's country), never a fixed one. */
  salesWord: string;
}

/** The section of `main-menu.ts` → the tone of its icons and the title of its label. */
const SECTION: Record<string, { tone: MenuTone; title: string }> = {
  'mainmenu.call-center': { tone: 'work', title: 'shell.sectionWork' },
  'mainmenu.control': { tone: 'ctrl', title: 'mainmenu.control' },
  'mainmenu.reports': { tone: 'rep', title: 'mainmenu.reports' },
};

const OLD_GROUP = 'old-pages';
const EXPANDED_GROUPS_KEY = 'cc.sidebar.expanded-groups';

/**
 * The new menu of the call-centre (the canvas, «Шапка и меню — редизайн», boards ShellMain and «Меню — состояния»): the brand, the shift card, the pages by sections (the same list, rights and order as the old menu —
 * `main-menu.ts`), «Старые страницы» that open and are remembered (the old menu's key), the version. Folded — a rail of icons; in a narrow window — a drawer over the page.
 */
@Component({
  selector: 'app-shell-sidebar',
  templateUrl: './shell-sidebar.component.html',
  styleUrls: ['./shell-sidebar.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, NgFor, RouterLink, RouterLinkActive, TranslocoPipe, CdkTrapFocus],
})
export class ShellSidebarComponent implements OnInit, OnDestroy {
  @HostBinding('attr.data-theme') themeAttr = 'light';
  @HostBinding('class.is-folded') folded = false;
  @HostBinding('class.is-drawer-open') drawer = false;

  readonly icon = SHELL_ICON;
  readonly version = environment.version;
  readonly profileUrl = `${location.protocol}//profile.${location.hostname.split('.').slice(1).join('.')}`;
  // the same rule as the old menu: active by the path, the query does not count (/faq?faq_id=…, the filters of «Заказы»)
  readonly activeOptions: IsActiveMatchOptions = { paths: 'exact', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' };
  rows: ShellNavRow[] = [];
  rail: ShellNavRow[] = [];
  oldRows: ShellNavRow[] = [];
  oldOpen = false;
  shift?: ShiftCardView;
  today?: TodayFigures;
  userName = '';
  initials = '';
  userMenu = false;
  /** The hint of the folded rail — drawn outside the scrolling list, so it is never cut. */
  tip?: { text: string; top: number; left: number };
  /** The rows are rebuilt each second of a call (its clock): the links stay the same elements — the hover, the focus and a click in flight survive. */
  readonly trackRow = (_: number, r: ShellNavRow): string => `${r.kind}:${r.link ?? r.title}`;

  private readonly ui = inject(ShellUiService);
  private readonly theme = inject(ThemeService);
  private readonly polls = inject(ShellPollsService);
  private readonly auth = inject(HxAuthService);
  private readonly router = inject(Router);
  private readonly tr = inject(TranslocoService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly network = inject(HeaderService);
  private readonly ticker = inject(CartTickerService);
  private readonly keycloak = inject(Keycloak);
  private readonly shellEnabled = inject(WORKPLACE_SHELL_ENABLED);
  private readonly callState = inject(CallStateService, { optional: true });
  private readonly drafts = inject(OrderDraftStore, { optional: true });
  private readonly callLink = inject(CallLinkService, { optional: true });
  private menu: MenuItem[] = buildMainMenu();
  private expanded: Record<string, boolean> = readExpandedGroups();
  private state?: CallShellState;
  private slot = '';
  private online = window.navigator.onLine ?? true;
  private busWasOpen = false;
  private late?: number;
  private url = '';
  private stopTicker?: () => void;
  /** What had the focus when the drawer opened (the button of the header): it gets it back when the drawer closes. */
  private drawerOpener?: HTMLElement;
  private subs = new Subscription();
  private readonly narrow = signal(false);

  constructor() {
    effect(() => {
      this.themeAttr = this.theme.theme();
      this.folded = this.ui.folded() && !this.narrow();
      const wasOpen = this.drawer;
      this.drawer = this.ui.drawer();
      if (this.drawer) {
        this.userMenu = false;
      }
      untracked(() => {
        this.cdr.markForCheck();
        if (this.drawer !== wasOpen) {
          this.moveFocus(this.drawer);
        }
      });
    });
    effect(() => {
      const late = this.polls.late();
      untracked(() => {
        this.late = late;
        this.build();
      });
    });
    effect(() => {
      this.polls.today();
      untracked(() => {
        this.drawToday();
        this.cdr.markForCheck();
      });
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.userMenu && !this.el.nativeElement.querySelector('.sb__shift-wrap')?.contains(event.target as Node)) {
      this.userMenu = false;
      this.cdr.markForCheck();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: Event): void {
    if (this.userMenu) {
      event.preventDefault();
      this.userMenu = false;
      this.cdr.markForCheck();
    } else if (this.drawer) {
      event.preventDefault();
      this.ui.closeDrawer();
    }
  }

  ngOnInit(): void {
    const media = window.matchMedia?.(NARROW_QUERY);
    if (media) {
      this.narrow.set(media.matches);
      const onChange = (e: MediaQueryListEvent) => this.narrow.set(e.matches);
      media.addEventListener?.('change', onChange);
      this.subs.add(() => media.removeEventListener?.('change', onChange));
    }
    const user = this.auth.user;
    this.userName = user.shortname || user.fullname || '';
    this.initials = initialsOf(user.fullname || user.shortname || '');
    this.slot = user.asteriskId ?? '';
    this.url = this.router.url;
    this.oldOpen = !!this.expanded[OLD_GROUP];
    this.polls.start();
    this.checkRights();
    if (this.callState && this.shellEnabled && WORKPLACE_READY) {
      this.subs.add(this.callState.state$.subscribe(state => {
        this.state = state;
        this.slot = state.slot ?? '';
        this.busWasOpen = this.busWasOpen || state.busOpen;
        this.refresh();
      }));
    }
    this.subs.add(this.network.network().subscribe(result => {
      this.online = result === 'online';
      this.refresh();
    }));
    this.subs.add(this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(e => {
      this.url = e.urlAfterRedirects;
      this.ui.closeDrawer();
      this.refresh();
    }));
    // the card is text of the dictionary drawn in code: drawn again when the dictionary arrives (it may come after the menu) and when the language changes
    this.subs.add(this.tr.events$.pipe(filter(e => e.type === 'translationLoadSuccess')).subscribe(() => {
      this.refresh();
      this.drawToday();
    }));
    this.subs.add(this.tr.langChanges$.subscribe(() => {
      this.refresh();
      this.drawToday();
    }));
    this.refresh();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.stopTicker?.();
  }

  toggleFolded(): void {
    this.userMenu = false;
    this.tip = undefined;
    this.ui.toggleFolded();
  }

  toggleOld(): void {
    if (this.folded) {
      this.ui.unfold();
      if (this.oldOpen) {
        return;
      }
    }
    this.oldOpen = !this.oldOpen;
    this.expanded = { ...this.expanded, [OLD_GROUP]: this.oldOpen };
    try {
      localStorage.setItem(EXPANDED_GROUPS_KEY, JSON.stringify(this.expanded));
    } catch {
      // without localStorage the group simply folds again after a reload
    }
  }

  toggleUserMenu(event: Event): void {
    event.stopPropagation();
    this.userMenu = !this.userMenu;
  }

  shiftAction(event: Event): void {
    event.stopPropagation();
    if (this.shift?.action?.act === 'pickNumber') {
      this.ui.openNumberDialog();
    } else if (this.shift?.action?.act === 'workplace') {
      void this.router.navigateByUrl(WORKPLACE_PATH);
    }
  }

  changeNumber(): void {
    this.userMenu = false;
    this.ui.openNumberDialog();
  }

  go(url: string): void {
    this.userMenu = false;
    void this.router.navigateByUrl(url);
  }

  closeDrawer(): void {
    this.ui.closeDrawer();
  }

  /** The hint of the rail: the page, and what its badge counts («Заказы · Опаздывают: 84»). */
  showTip(event: Event, row: ShellNavRow): void {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const title = this.tr.translate(row.title);
    const text = row.badge ? `${title} · ${this.tr.translate('shell.late', { n: row.badge })}` : row.live ? `${title} · ${row.live}` : title;
    this.tip = { text, top: rect.top + rect.height / 2, left: rect.right + 10 };
  }

  hideTip(): void {
    this.tip = undefined;
  }

  logout(): void {
    // the drafts of «Новый заказ» in this tab hold clients' numbers: they do not outlive the session (security 2a1-P3-11) — as the old header does
    this.drafts?.purgeAll();
    this.callState?.purge();
    this.callLink?.purge();
    void this.keycloak.logout();
  }

  /** The rows of the menu with rights are shown only when the user has one of them — the rule of the old menu. */
  private checkRights(): void {
    const withRights = this.menu.filter(m => m.permissions && m.permissions.length > 0);
    const keys = [...new Set(withRights.flatMap(m => m.permissions ?? []))];
    this.build();
    if (!keys.length) {
      return;
    }
    this.auth.getPermissions({ permissions: keys as PermissionKey[] }).subscribe(result => {
      const granted = (result ?? []).map(r => r.key);
      withRights.forEach(m => {
        if (m.permissions?.some(p => granted.includes(p))) {
          m.visible = true;
        }
      });
      this.build();
    });
  }

  /** `tick`: the one-second ticker runs outside the zone — the view is drawn at once, and only when a figure of the card changed. */
  private refresh(tick = false): void {
    const view = buildShiftCardView({
      state: this.state,
      slot: this.slot,
      position: this.auth.user.position || this.tr.translate('header.noPosition'),
      online: this.online && !(this.busWasOpen && this.state && !this.state.busOpen),
      url: this.url,
      nowMs: performance.now(),
      wallMs: Date.now(),
      lang: this.tr.getActiveLang(),
      tr: (key, params) => this.tr.translate(key, params),
    });
    const before = this.shift;
    this.shift = view;
    const changed = !before || before.live !== view.live || before.kind !== view.kind || before.title !== view.title || before.sub !== view.sub;
    if (changed) {
      this.build();
    }
    const ticking = view.kind === 'ringing' || view.kind === 'talking' || view.kind === 'finished';
    if (ticking && !this.stopTicker) {
      this.stopTicker = this.ticker.subscribe(() => this.refresh(true));
    } else if (!ticking && this.stopTicker) {
      this.stopTicker();
      this.stopTicker = undefined;
    }
    if (tick) {
      if (changed) {
        this.cdr.detectChanges();
      }
    } else {
      this.cdr.markForCheck();
    }
  }

  private build(): void {
    const rows: ShellNavRow[] = [];
    const old: ShellNavRow[] = [];
    let tone: MenuTone = 'work';
    let oldVisible = false;
    for (const m of this.menu) {
      if (!m.visible) {
        continue;
      }
      if (m.isSub && m.group === OLD_GROUP) {
        oldVisible = true;
        continue;
      }
      if (m.isSub) {
        const section = SECTION[m.title] ?? { tone: 'work', title: m.title };
        tone = section.tone;
        rows.push({ kind: 'label', title: section.title, icon: '', tone });
        continue;
      }
      const isOld = m.group === OLD_GROUP;
      const row: ShellNavRow = {
        kind: 'item', title: m.title, link: `/${m.link ?? m.title}`, queryParams: m.queryParams, icon: iconOfLink(m.link, isOld), tone: isOld ? 'old' : tone,
        live: m.link === 'call-center' ? this.shift?.live : undefined,
        badge: m.link === 'orders' && this.late ? this.late : undefined,
      };
      (isOld ? old : rows).push(row);
    }
    if (oldVisible) {
      rows.push({ kind: 'label', title: 'shell.sectionMore', icon: '', tone: 'old' });
      rows.push({ kind: 'oldToggle', title: 'mainmenu.old-pages', icon: SHELL_ICON.old, tone: 'old' });
    }
    this.rows = rows;
    this.oldRows = old;
    this.rail = rows.filter(r => r.kind !== 'label').map((r, i, all) => ({ ...r, sep: i > 0 && all[i - 1].tone !== r.tone }));
    this.cdr.markForCheck();
  }

  /** The drawer is a modal over the page: opened — the focus goes to its «Закрыть» (and stays inside, `cdkTrapFocus`); closed — back where it came from, unless it already moved elsewhere on the page. */
  private moveFocus(open: boolean): void {
    const active = document.activeElement as HTMLElement | null;
    if (open) {
      this.drawerOpener = active ?? undefined;
      setTimeout(() => (this.el.nativeElement as HTMLElement).querySelector<HTMLElement>('.sb__close')?.focus());
      return;
    }
    const back = this.drawerOpener;
    this.drawerOpener = undefined;
    if (back?.isConnected && (!active || active === document.body || (this.el.nativeElement as HTMLElement).contains(active))) {
      back.focus();
    }
  }

  private drawToday(): void {
    const today = this.polls.today();
    this.today = today ? this.figures(today) : undefined;
  }

  private figures(today: MyTodayModel): TodayFigures {
    const lang = this.tr.getActiveLang();
    const calls = today.answeredCalls;
    const orders = today.createdCount;
    return {
      calls: String(calls),
      callsWord: pluralParams(lang, calls, CALL_WORD).word,
      orders: String(orders),
      ordersWord: pluralParams(lang, orders, ORDER_WORD).word,
      sales: Math.round(today.createdRevenue).toLocaleString('ru-RU').replace(/ /g, ' '),
      salesWord: this.tr.translate('shell.shift.sales', { currency: currencySymbol(today.currency) }).trim(),
    };
  }
}

function initialsOf(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map(p => p.charAt(0).toUpperCase()).join('');
}

function readExpandedGroups(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(EXPANDED_GROUPS_KEY) ?? '{}') ?? {};
  } catch {
    return {};
  }
}
